import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Observable } from 'rxjs/internal/Observable';
import { finalize, map, mergeMap } from 'rxjs/operators';

import { NgxRole, NgxRolesService } from 'ngx-permissions';

import { RequestIdService } from './request-id.service';
import { UserService } from './user.service';

import { CodeVerifyModel, UserMeModel, UserService as ApiUserService } from '@core/api';
import { User } from '../../models/user';

import { environment } from '../../../../environments/environment';
import { Subject } from 'rxjs/internal/Subject';
import { UserSession } from '@core/api/model/userSession';
import {PasswordVerifyModel} from "@core/api/model/passwordVerifyModel";

const TOKEN = environment.tokenStorageKey;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentTokenSubject: BehaviorSubject<string>;
  public currentToken: Observable<string>;

  private authorizeSubject: Subject<null> = new Subject<null>();
  public authorize$: Observable<null> = this.authorizeSubject.asObservable();

  private logoutSubject: Subject<null> = new Subject<null>();
  public logout$: Observable<null> = this.logoutSubject.asObservable();

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private rolesService: NgxRolesService,
    private apiUserService: ApiUserService,
    private requestIdService: RequestIdService
  ) {
    this.currentTokenSubject = new BehaviorSubject<string>(localStorage.getItem(TOKEN));
    this.currentToken = this.currentTokenSubject.asObservable();
  }

  private addRoles() {
    const validationFunction = (role) => {
      const currentRole = this.userService.currentUserValue ? this.userService.currentUserValue.role : UserMeModel.RoleEnum.GUEST;

      return currentRole === role;
    };

    this.rolesService.flushRoles();

    return this.rolesService.addRoles(
      {
        [UserMeModel.RoleEnum.GUEST]: () => validationFunction(UserMeModel.RoleEnum.GUEST),
        [UserMeModel.RoleEnum.ADMIN]: () => validationFunction(UserMeModel.RoleEnum.ADMIN),
        [UserMeModel.RoleEnum.OPERATOR]: () => validationFunction(UserMeModel.RoleEnum.OPERATOR),
        [UserMeModel.RoleEnum.TECHNICAL_OPERATOR]: () => validationFunction(UserMeModel.RoleEnum.TECHNICAL_OPERATOR),
        [UserMeModel.RoleEnum.TECHNICAL_SUPPORT]: () => validationFunction(UserMeModel.RoleEnum.TECHNICAL_SUPPORT),
        [UserMeModel.RoleEnum.USER]: () => validationFunction(UserMeModel.RoleEnum.USER)
      }
    );
  }

  private setUser(user: User) {
    this.userService.currentUserValue = user;

    this.addRoles();
  }

  public get currentTokenValue(): string {
    return this.currentTokenSubject.value;
  }

  public set currentTokenValue(token: string) {
    if (token) {
      localStorage.setItem(TOKEN, token);
      this.currentTokenSubject.next(token);
    } else {
      localStorage.removeItem(TOKEN);
      this.currentTokenSubject.next(null);
    }
  }

  get currentRole(): NgxRole {
    const roles = this.rolesService.getRoles();

    return Object.values(roles).find(role => {
      return typeof role.validationFunction === 'function' && role.validationFunction();
    });
  }

  authorize() {
    return this.currentTokenValue && this.apiUserService.userMeGet()
      .toPromise()
      .then(res => {
        this.setUser(res.data);
        this.authorizeSubject.next();
      })
      .catch((err: any) => {
        this.setUser(null);
        Promise.resolve();
      });
  }

  updateExtendAt(): Observable<UserSession> {
    return this.apiUserService.updateExtendSession()
      .pipe(map(res => res.data));
  }

  login(phone: string) {
    return this.apiUserService.userLoginPost({
      phone_number: phone,
      country_code: '375'
    }).pipe(map(this.requestIdService.save(phone)));
  }

  loginByPassword(phoneNumber: string, password: string, rememberMe: boolean) {
    const passwordVerify: PasswordVerifyModel = {
      phone_number: phoneNumber,
      password,
      country_code: '375',
      remember_me: rememberMe
    };

    return this.apiUserService.userLoginByPasswordPost(passwordVerify)
      .pipe(mergeMap(({ data }) => {
        this.currentTokenValue = data.token;

        return this.authorize();
      }));
  }

  login2faVerify(code: string) {

    const codeVerify: CodeVerifyModel = {
      request_id: this.requestIdService.value.requestId,
      phone_number: this.requestIdService.value.phoneNumber,
      code
    };

    return this.apiUserService.userLogin2faVerifyPost(codeVerify)
      .pipe(mergeMap(({ data }) => {
        this.currentTokenValue = data.token;

        return this.authorize();
      }));
  }

  loginSocialNetworkVerify(network: number, sid: string) {
    return this.apiUserService.userLoginSocialNetworkVerifyPost({network: network, sid: sid})
        .pipe(mergeMap(({ data }) => {
          this.currentTokenValue = data.token;

          return this.authorize();
        }));
  }

  reset() {
    this.setUser(null);
    this.currentTokenValue = null;

    return true;
  }

  logout() {
    return this.apiUserService.userLogoutPost()
      .pipe(finalize(() => {
        this.logoutSubject.next();
        this.reset();
      }));
  }
}
